import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { AutoScheduleNudgeService } from 'src/app/shared/services/auto-schedule-nudge.service';

/**
 * Low-emphasis inline banner rendered inside the Climate card, above the room
 * temperature cards. Driven by AutoScheduleNudgeService.nudge$.
 *
 * Because it sits at the top of the card, appearing/disappearing would normally
 * push the room cards + controls down/up. To keep those visually fixed, we toggle
 * visibility manually and cancel the shift with a scroll adjustment (FLIP: measure
 * the sibling controls' position, toggle, measure again, scroll the page by the
 * exact delta).
 */
@Component({
  selector: 'auto-schedule-nudge',
  templateUrl: './auto-schedule-nudge.component.html',
  styleUrls: ['./auto-schedule-nudge.component.scss'],
})
export class AutoScheduleNudgeComponent implements OnInit, OnDestroy {
  public visible = false;

  /** The page's scroll element, cached so compensation is synchronous (flicker-free). */
  private scrollEl: HTMLElement | null = null;
  /** First emission establishes baseline layout — nothing to preserve, so no scroll. */
  private initialized = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private nudge: AutoScheduleNudgeService,
    private router: Router,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const content = document.querySelector('ion-content') as
      | (HTMLElement & { getScrollElement?: () => Promise<HTMLElement> })
      | null;
    content?.getScrollElement?.()
      .then((el) => (this.scrollEl = el))
      .catch(() => undefined);

    this.nudge.nudge$
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((v) => this.applyVisibility(v));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setUp(): void {
    this.nudge.onSetUpTapped();
    this.router.navigateByUrl('/dashboard/settings/climate');
  }

  onDismiss(event: Event): void {
    event.stopPropagation();
    void this.nudge.dismissNudge();
  }

  /**
   * Toggle the banner and cancel the layout shift it causes. The next element
   * sibling is the `.thermostats-container` (room cards + controls); we scroll the
   * page by the exact amount it moved so it stays put on screen. Falls back to a
   * plain toggle when there's no scroll container or sibling (e.g. unit tests).
   */
  private applyVisibility(next: boolean): void {
    if (!this.initialized) {
      this.initialized = true;
      this.visible = next;
      return;
    }
    if (next === this.visible) return;

    const anchor = this.host.nativeElement.nextElementSibling;
    const before = anchor?.getBoundingClientRect().top ?? 0;

    this.visible = next;
    this.cdr.detectChanges();

    const after = anchor?.getBoundingClientRect().top ?? 0;
    const delta = after - before;
    if (this.scrollEl && delta !== 0) {
      this.scrollEl.scrollTop += delta;
    }
  }
}
